import { useState, useEffect, useMemo } from 'react';
import { getApiBaseUrl } from '../../../utils/apiConfig';

const DEFAULT_API = getApiBaseUrl();

/**
 * URLs de foto: prioriza profilePhotoUrl já na listagem; só busca /urls para ids faltantes.
 * @param {Array<number|string|{id?: number}>} usersOrIds
 * @param {{ apiBaseUrl?: string, enabled?: boolean }} [opts]
 */
export function useUserProfilePhotos(usersOrIds, opts = {}) {
  const apiBaseUrl = opts.apiBaseUrl || DEFAULT_API;
  const enabled = opts.enabled !== false;

  const { seeded, missingIds } = useMemo(() => {
    const list = Array.isArray(usersOrIds) ? usersOrIds : [];
    const seededMap = {};
    const missing = [];
    const seen = new Set();

    for (const item of list) {
      const id = typeof item === 'object' && item != null ? Number(item.id) : Number(item);
      if (!Number.isFinite(id) || id <= 0 || seen.has(id)) continue;
      seen.add(id);

      const embedded =
        typeof item === 'object' && item != null
          ? String(item.profilePhotoUrl || item.viewUrl || '').trim()
          : '';
      if (embedded) {
        seededMap[String(id)] = embedded;
        continue;
      }

      const hasPhoto =
        typeof item === 'object' && item != null
          ? item.hasProfilePhoto === true ||
            Boolean(String(item.profile_photo_s3_key || item.profilePhotoS3Key || '').trim())
          : true;
      if (!hasPhoto) continue;
      missing.push(id);
    }

    return { seeded: seededMap, missingIds: missing };
  }, [usersOrIds]);

  const [fetchedMap, setFetchedMap] = useState({});
  const missingKey = missingIds.join(',');

  useEffect(() => {
    if (!enabled || !apiBaseUrl || !missingKey) {
      setFetchedMap({});
      return undefined;
    }

    let cancelled = false;
    const token = localStorage.getItem('authToken');
    const ids = missingKey.split(',').map((x) => Number(x)).filter((n) => Number.isFinite(n));

    const load = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/users/profile-photos/urls`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ ids }),
        });
        const data = await res.json();
        if (!cancelled && data?.success && data?.urls && typeof data.urls === 'object') {
          setFetchedMap(data.urls);
          return;
        }
      } catch {
        /* fallback abaixo */
      }

      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const res = await fetch(`${apiBaseUrl}/api/users/${id}/profile-photo/url`, {
              headers: { Authorization: `Bearer ${token}` },
              credentials: 'include',
            });
            const data = await res.json();
            return [String(id), data.success && data.viewUrl ? data.viewUrl : null];
          } catch {
            return [String(id), null];
          }
        })
      );
      if (!cancelled) setFetchedMap(Object.fromEntries(entries));
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, enabled, missingKey]);

  return useMemo(() => ({ ...seeded, ...fetchedMap }), [seeded, fetchedMap]);
}

/** Busca uma única URL de foto (login / header). Preferir profilePhotoUrl do verify quando existir. */
export async function fetchUserProfilePhotoUrl(userId, apiBaseUrl = DEFAULT_API) {
  const id = Number(userId);
  if (!Number.isFinite(id) || id <= 0) return null;
  const token = localStorage.getItem('authToken');
  const res = await fetch(`${apiBaseUrl}/api/users/${id}/profile-photo/url`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  });
  const data = await res.json();
  return data?.success && data.viewUrl ? data.viewUrl : null;
}
